import {
  createRouter,
  createWebHashHistory,
  RouteRecordRaw,
} from "vue-router";
import store from "@/plugins/store/store";
import i18n from "@/plugins/i18n";
import Roles, { toRoleName } from "@/constants/Roles";
import { applyPageBranding } from "@/utils/branding";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/login",
    name: "UserLogin",
    component: () =>
      import(/* webpackMode: "lazy" */ "../views/Auth/UserLogin.vue"),
    meta: {
      title: "login",
    },
  },
  {
    path: "/password-recover",
    name: "password-recover",
    component: () =>
      import(
        /* webpackMode: "lazy" */ "../views/Auth/PasswordRecover.vue"
      ),
    meta: {
      title: "recover",
    },
  },
  {
    path: "/",
    component: () => import(/* webpackMode: "lazy" */ "../views/Base.vue"),
    meta: {
      title: "users",
      auth: true,
    },
    redirect: () => {
      const currentUser = store.getters["auth/currentUser"];
      const role = toRoleName(currentUser?.role);
      if (role === Roles.Seller) return { name: "contacts" };
      if (role === Roles.Client) return { name: "customer-loans" };
      return { name: "dashboard" };
    },
    children: [
      {
        path: "customer/loans",
        name: "customer-loans",
        component: () =>
          import(/* webpackMode: "lazy" */ "../views/CustomerPortal/LoansIndex.vue"),
        meta: {
          title: "customer-loans",
          auth: true,
          roles: [Roles.Client],
        },
      },
      {
        path: "customer/loans/:id",
        name: "customer-loans-details",
        component: () =>
          import(/* webpackMode: "lazy" */ "../views/CustomerPortal/LoanDetails.vue"),
        meta: {
          title: "customer-loans-details",
          auth: true,
          roles: [Roles.Client],
        },
      },
      {
        path: "customer/contracts",
        name: "customer-contracts",
        component: () =>
          import(/* webpackMode: "lazy" */ "../views/CustomerPortal/ContractsIndex.vue"),
        meta: {
          title: "customer-contracts",
          auth: true,
          roles: [Roles.Client],
        },
      },
      {
        path: "customer/contracts/:id",
        name: "customer-contracts-details",
        component: () =>
          import(/* webpackMode: "lazy" */ "../views/CustomerPortal/ContractDetails.vue"),
        meta: {
          title: "customer-contracts-details",
          auth: true,
          roles: [Roles.Client],
        },
      },
      {
        path: "dashboard",
        name: "dashboard",
        component: () =>
          import(/* webpackMode: "lazy" */ "../views/Dashboard/Index.vue"),
        meta: {
          title: "dashboard",
          auth: true,
          roles: [Roles.Admin, Roles.Dealer, Roles.DealerAdmin],
        },
      },
      {
        path: "users",
        name: "users",
        component: () => import(/* webpackMode: "lazy" */ "../views/Users/Index.vue"),
        meta: {
          title: "users",
          auth: true,
          roles: [Roles.Admin, Roles.DealerAdmin],
        },
      },
      {
        path: "users/create",
        name: "users-create",
        component: () => import(/* webpackMode: "lazy" */ "../views/Users/Form.vue"),
        meta: {
          title: "users-create",
          auth: true,
          roles: [Roles.Admin, Roles.DealerAdmin],
        },
      },
      {
        path: "users/:id",
        name: "users-details",
        component: () => import(/* webpackMode: "lazy" */ "../views/Users/Details.vue"),
        meta: {
          title: "users-details",
          auth: true,
          roles: [Roles.Admin, Roles.DealerAdmin],
        },
      },
      {
        path: "users/:id/edit",
        name: "users-edit",
        component: () => import(/* webpackMode: "lazy" */ "../views/Users/Form.vue"),
        meta: {
          title: "users-edit",
          auth: true,
          roles: [Roles.Admin, Roles.DealerAdmin],
        },
      },
      {
        path: "dealers",
        name: "dealers",
        component: () => import(/* webpackMode: "lazy" */ "../views/Dealers/Index.vue"),
        meta: {
          title: "dealers",
          auth: true,
          requiresAdmin: true,
          roles: [Roles.Admin],
        },
      },
      {
        path: "dealers/create",
        name: "dealers-create",
        component: () => import(/* webpackMode: "lazy" */ "../views/Dealers/Form.vue"),
        meta: {
          title: "dealers-create",
          auth: true,
          requiresAdmin: true,
          roles: [Roles.Admin],
        },
      },
      {
        path: "dealers/:id",
        name: "dealers-details",
        component: () => import(/* webpackMode: "lazy" */ "../views/Dealers/Details.vue"),
        meta: {
          title: "dealers-details",
          auth: true,
          requiresAdmin: true,
          roles: [Roles.Admin],
        },
      },
      {
        path: "dealers/:id/edit",
        name: "dealers-edit",
        component: () => import(/* webpackMode: "lazy" */ "../views/Dealers/Edit.vue"),
        meta: {
          title: "dealers-edit",
          auth: true,
          requiresAdmin: true,
          roles: [Roles.Admin],
        },
      },
      {
        path: "dealers/:id/branding",
        name: "dealers-branding",
        component: () => import(/* webpackMode: "lazy" */ "../views/Dealers/Branding.vue"),
        meta: {
          title: "branding",
          auth: true,
          requiresAdmin: true,
          roles: [Roles.Admin],
        },
      },
      {
        path: "branding",
        name: "branding",
        component: () => import(/* webpackMode: "lazy" */ "../views/Dealers/Branding.vue"),
        meta: {
          title: "branding",
          auth: true,
          roles: [Roles.DealerAdmin],
        },
      },
      {
        path: "change-password",
        name: "change-password",
        component: () => import(/* webpackMode: "lazy" */ "../views/Settings/ChangePassword.vue"),
        meta: {
          title: "change-password",
          auth: true,
          roles: [Roles.Admin, Roles.Dealer, Roles.DealerAdmin, Roles.Seller, Roles.Client],
        },
      },
      {
        path: "dealers/:id/info",
        name: "dealers-info",
        component: () => import(/* webpackMode: "lazy" */ "../views/Dealers/DealerInfo.vue"),
        meta: {
          title: "dealer-info",
          auth: true,
          requiresAdmin: true,
          roles: [Roles.Admin],
        },
      },
      {
        path: "dealer-info",
        name: "dealer-info",
        component: () => import(/* webpackMode: "lazy" */ "../views/Dealers/DealerInfo.vue"),
        meta: {
          title: "dealer-info",
          auth: true,
          roles: [Roles.Dealer, Roles.DealerAdmin, Roles.Seller, Roles.Client],
        },
      },
      {
        path: "loans",
        name: "loans",
        component: () => import(/* webpackMode: "lazy" */ "../views/Loans/Index.vue"),
        meta: {
          title: "loans",
          auth: true,
          roles: [Roles.Admin, Roles.Dealer, Roles.DealerAdmin],
        },
      },
      {
        path: "loans/create",
        name: "loans-create",
        component: () => import(/* webpackMode: "lazy" */ "../views/Loans/Form.vue"),
        meta: {
          title: "loans-create",
          auth: true,
          roles: [Roles.Admin, Roles.Dealer, Roles.DealerAdmin],
        },
      },
      {
        path: "loans/:id",
        name: "loans-details",
        component: () => import(/* webpackMode: "lazy" */ "../views/Loans/Details.vue"),
        meta: {
          title: "loans-details",
          auth: true,
          roles: [Roles.Admin, Roles.Dealer, Roles.DealerAdmin],
        },
      },
      {
        path: "loans/:id/delinquency",
        name: "loans-delinquency",
        component: () => import(/* webpackMode: "lazy" */ "../views/Loans/Delinquency.vue"),
        meta: {
          title: "loans-delinquency",
          auth: true,
          roles: [Roles.Admin, Roles.Dealer, Roles.DealerAdmin],
        },
      },
      {
        path: "reports",
        name: "reports",
        component: () => import(/* webpackMode: "lazy" */ "../views/Reports/Index.vue"),
        meta: {
          title: "reports",
          auth: true,
          roles: [Roles.Admin, Roles.DealerAdmin],
        },
      },
      {
        path: "contacts",
        name: "contacts",
        component: () => import(/* webpackMode: "lazy" */ "../views/Contacts/Index.vue"),
        meta: {
          title: "contacts",
          auth: true,
          roles: [Roles.Admin, Roles.Dealer, Roles.DealerAdmin, Roles.Seller],
        },
      },
      {
        path: "contacts/create",
        name: "contacts-create",
        component: () => import(/* webpackMode: "lazy" */ "../views/Contacts/Form.vue"),
        meta: {
          title: "contacts-create",
          auth: true,
          roles: [Roles.Admin, Roles.Dealer, Roles.DealerAdmin, Roles.Seller],
        },
      },
      {
        path: "contacts/:id",
        name: "contacts-details",
        component: () => import(/* webpackMode: "lazy" */ "../views/Contacts/Details.vue"),
        meta: {
          title: "contacts-details",
          auth: true,
          roles: [Roles.Admin, Roles.Dealer, Roles.DealerAdmin, Roles.Seller],
        },
      },
      {
        path: "contacts/:id/edit",
        name: "contacts-edit",
        component: () => import(/* webpackMode: "lazy" */ "../views/Contacts/Form.vue"),
        meta: {
          title: "contacts-edit",
          auth: true,
          roles: [Roles.Admin, Roles.Dealer, Roles.DealerAdmin, Roles.Seller],
        },
      },
      {
        path: "vehicles",
        name: "vehicles",
        component: () => import(/* webpackMode: "lazy" */ "../views/Vehicles/Index.vue"),
        meta: {
          title: "vehicles",
          auth: true,
          roles: [Roles.Admin, Roles.Dealer, Roles.DealerAdmin, Roles.Seller],
        },
      },
      {
        path: "vehicles/create",
        name: "vehicles-create",
        component: () => import(/* webpackMode: "lazy" */ "../views/Vehicles/Form.vue"),
        meta: {
          title: "vehicles-create",
          auth: true,
          roles: [Roles.Admin, Roles.Dealer, Roles.DealerAdmin, Roles.Seller],
        },
      },
      {
        path: "vehicles/:id",
        name: "vehicles-details",
        component: () => import(/* webpackMode: "lazy" */ "../views/Vehicles/Details.vue"),
        meta: {
          title: "vehicles-details",
          auth: true,
          roles: [Roles.Admin, Roles.Dealer, Roles.DealerAdmin, Roles.Seller],
        },
      },
      {
        path: "vehicles/:id/edit",
        name: "vehicles-edit",
        component: () => import(/* webpackMode: "lazy" */ "../views/Vehicles/Form.vue"),
        meta: {
          title: "vehicles-edit",
          auth: true,
          roles: [Roles.Admin, Roles.Dealer, Roles.DealerAdmin],
        },
      },
      {
        path: "contracts",
        name: "contracts",
        component: () => import(/* webpackMode: "lazy" */ "../views/Contracts/Index.vue"),
        meta: {
          title: "contracts",
          auth: true,
          roles: [Roles.Admin, Roles.Dealer, Roles.DealerAdmin],
        },
      },
      {
        path: "contracts/create",
        name: "contracts-create",
        component: () => import(/* webpackMode: "lazy" */ "../views/Contracts/Form.vue"),
        meta: {
          title: "contracts-create",
          auth: true,
          roles: [Roles.Admin, Roles.Dealer, Roles.DealerAdmin],
        },
      },
      {
        path: "contracts/:id",
        name: "contracts-details",
        component: () => import(/* webpackMode: "lazy" */ "../views/Contracts/Details.vue"),
        meta: {
          title: "contracts-details",
          auth: true,
          roles: [Roles.Admin, Roles.Dealer, Roles.DealerAdmin],
        },
      },
      {
        path: "contracts/:id/edit",
        name: "contracts-edit",
        component: () => import(/* webpackMode: "lazy" */ "../views/Contracts/Form.vue"),
        meta: {
          title: "contracts-edit",
          auth: true,
          roles: [Roles.Admin, Roles.Dealer, Roles.DealerAdmin],
        },
      },
      {
        path: "garages",
        name: "garages",
        component: () => import(/* webpackMode: "lazy" */ "../views/Garages/Index.vue"),
        meta: {
          title: "garages",
          auth: true,
          roles: [Roles.Admin, Roles.DealerAdmin],
        },
      },
      {
        path: "garages/create",
        name: "garages-create",
        component: () => import(/* webpackMode: "lazy" */ "../views/Garages/Form.vue"),
        meta: {
          title: "garages-create",
          auth: true,
          roles: [Roles.Admin, Roles.DealerAdmin],
        },
      },
      {
        path: "garages/:id",
        name: "garages-details",
        component: () => import(/* webpackMode: "lazy" */ "../views/Garages/Details.vue"),
        meta: {
          title: "garages-details",
          auth: true,
          roles: [Roles.Admin, Roles.DealerAdmin],
        },
      },
      {
        path: "garages/:id/edit",
        name: "garages-edit",
        component: () => import(/* webpackMode: "lazy" */ "../views/Garages/Form.vue"),
        meta: {
          title: "garages-edit",
          auth: true,
          roles: [Roles.Admin, Roles.DealerAdmin],
        },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHashHistory(process.env.BASE_URL),
  routes,
});

function getRoleHome(role: string): string {
  if (role === Roles.Seller) return "contacts";
  if (role === Roles.Client) return "customer-loans";
  return "dashboard";
}

router.beforeEach((to, _from, next) => {
  const isAuthenticated = !!store.getters["auth/isAuthenticated"];
  const currentUser = store.getters["auth/currentUser"];
  const userRole = currentUser?.role || "";

  if (to.matched.some((record) => record.meta.auth)) {
    if (!isAuthenticated) {
      return next({ path: "/login", query: { redirect: to.fullPath } });
    }

    const requiredRoles: string[] = to.matched
      .flatMap((r) =>
        r.meta && (r.meta as any).roles ? (r.meta as any).roles : []
      )
      .map((v: any) => String(v))
      .filter((v: string) => v.length > 0);

    if (requiredRoles.length > 0) {
      if (!userRole || !requiredRoles.includes(userRole)) {
        // Redirect to role home instead of logging out
        return next({ name: getRoleHome(userRole) });
      }
    }

    if (to.matched.some((record) => record.meta.requiresAdmin)) {
      if (!userRole || userRole !== Roles.Admin) {
        return next({ name: getRoleHome(userRole) });
      }
    }

    return next();
  }

  if (to.name === "UserLogin" && isAuthenticated) {
    return next({ name: getRoleHome(userRole) });
  }

  if (!isAuthenticated && to.path === "/") {
    return next({ path: "/login" });
  }

  return next();
});

router.afterEach((to) => {
  const routeNameKey = String(to.meta.title || to.name || "");
  const translatedRouteName = String(i18n.global.t(routeNameKey));
  const routeTitle =
    translatedRouteName && translatedRouteName !== routeNameKey
      ? translatedRouteName
      : routeNameKey;
  const currentUser = store.getters["auth/currentUser"];
  const isSuperAdmin = toRoleName(currentUser?.role) === Roles.Admin;

  applyPageBranding(routeTitle, store.getters["dealers/myDealer"], {
    isSuperAdmin,
  });
});

export default router;
